import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { ItemInfo } from "../../types/ItemInfo";
import { ProfileInventoryItem } from "../ProfileInventory";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import bulletImg from "../../assets/imgs/slot/bullets.png";
import { ItemCategoryInfoList } from "../../constants/const";
import { ItemCategory } from "../../constants/enum/enum";
import { toastSuccess, toUSDFormat } from "../../utils/utils";

import { Messages } from "../../constants/messages";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useWallet from "../../hook/useWallet";
import {
  getBulletBalance,
  getMyInventoryItems,
  unwrapBulletAction,
} from "../../reducers/profile.slice";
import useStyles from "./BulletDepositPopupStyles";

interface BulletDepositPopupProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const BulletDepositPopup: React.FC<BulletDepositPopupProps> = ({
  openModal,
  setOpenModal,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { account } = useWallet();

  const { myInventoryItems, myProfile } = useAppSelector(
    (state) => state.profile
  );
  const { signMsg, signature } = useAppSelector((state) => state.auth);

  const [selectedItems, setSelectedItems] = useState<ItemInfo[]>([]);

  const totalBulletCount = useMemo(() => {
    return selectedItems.reduce(
      (total, item) =>
        total +
        (ItemCategoryInfoList[ItemCategory.BULLET].values[
          item.typeId
        ] as number),
      0
    );
  }, [selectedItems]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleDeposit = () => {
    if (selectedItems.length === 0) return;

    dispatchTxAction(
      dispatch,
      unwrapBulletAction({
        account,
        itemIds: selectedItems.map((item) => item.itemId),
      }),
      () => {
        toastSuccess(Messages.FAMILY.INVENTORY.UNWRAP_BULLET_SUCCESS);

        if (myProfile.id && myProfile.address) {
          dispatch(getMyInventoryItems({ userId: myProfile.id }));
          dispatch(getBulletBalance({ account, signMsg, signature }));
          handleClose();
        }
      }
    );
  };

  const renderItems = useCallback(() => {
    return (
      <>
        {myInventoryItems
          .filter((item) => item.categoryId === ItemCategory.BULLET)
          .map((item, index) => {
            const indexInSelectedItems = selectedItems
              .map((item) => item.itemId)
              .indexOf(item.itemId);

            return (
              <ProfileInventoryItem
                onClick={() => {
                  if (indexInSelectedItems >= 0)
                    setSelectedItems((items) => {
                      return [
                        ...items.slice(0, indexInSelectedItems),
                        ...items.slice(indexInSelectedItems + 1, items.length),
                      ];
                    });
                  else {
                    setSelectedItems((items) => [...items, item]);
                  }
                }}
                item={item}
                key={index}
                selected={indexInSelectedItems >= 0}
              />
            );
          })}
      </>
    );
  }, [myInventoryItems, selectedItems]);

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.body}>
      <Box className={classes.modalContent}>
        <Box className={classes.closeIconBody}>
          <Button onClick={handleClose}>
            <CloseIcon htmlColor="gray" />
          </Button>
        </Box>

        <Box className={classes.contentBody}>
          <Box component={"img"} src={bulletImg} className={classes.logoImg} />
          <Box className={classes.titleText}>Deposit Bullet item</Box>
          <Box className={classes.inventoryTitle}>Your inventory</Box>

          <Box className={classes.inventoryBox}>{renderItems()}</Box>

          <Button
            variant="contained"
            className={classes.confirmBut}
            onClick={handleDeposit}
          >
            Deposit {toUSDFormat(totalBulletCount)} bullets
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BulletDepositPopup;
